import { useMutation, useQuery } from '@tanstack/react-query';
import { confirmTotpSetup, startTotpSetup } from '@/api/auth.api';

/**
 * The QR step's secret for an in-flight login (MYN-241).
 *
 * A query, not a mutation, on purpose: every start call makes Cognito issue a
 * NEW secret, so it must run exactly once per sign-in. One cache key per
 * pre-auth token dedupes StrictMode's double mount, and the refetch triggers
 * are off so a tab refocus cannot silently invalidate the QR on screen.
 */
export function useTotpSetupStart(preAuthToken: string, recoveryCode?: string) {
  return useQuery({
    // The recovery code is part of the key: after an abandoned re-scan the
    // first start is refused, and entering a code is a new, single start (FR-221).
    queryKey: ['auth', 'totp-setup', preAuthToken, recoveryCode ?? null],
    queryFn: () => startTotpSetup(preAuthToken, recoveryCode),
    enabled: Boolean(preAuthToken),
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}

/**
 * Confirm setup with the first code. Deliberately does NOT complete the
 * login: the page shows the recovery codes first and finishes the session
 * only once the user has saved them.
 */
export function useConfirmTotpSetup(preAuthToken: string) {
  return useMutation({
    mutationFn: (code: string) => confirmTotpSetup({ code, preAuthToken }),
  });
}
